import React, { useState, useEffect } from 'react';

export default function KycDocuments() {
  const [kycRequests, setKycRequests] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [statusMessage, setStatusMessage] = useState({ type: '', message: '' });
  
  // Add new state variables for fee dialog
  const [isFeeDialogOpen, setIsFeeDialogOpen] = useState(false);
  const [pendingStatusChange, setPendingStatusChange] = useState(null);
  const [customFees, setCustomFees] = useState({
    platformFee: '5',
    monthlyFee: '5'
  });

  const API_URL = process.env.REACT_APP_API_URL || 'https://didrouteapi.com/api';

  useEffect(() => {
    fetchKycRequests();
  }, [currentPage, selectedStatus]);

  useEffect(() => {
    const handleEsc = (event) => {
      if (event.key === 'Escape') {
        setIsModalOpen(false);
        setSelectedRequest(null);
      }
    };
    window.addEventListener('keydown', handleEsc);

    return () => {
      window.removeEventListener('keydown', handleEsc);
    };
  }, []);

  const fetchKycRequests = async () => {
    try {
      setIsLoading(true);
      const token = localStorage.getItem('superadminToken');
      if (!token) {
        alert('No authentication token found. Please login again.');
        return;
      }

      const response = await fetch(
        `${API_URL}/kyc/admin/requests?status=${selectedStatus}&page=${currentPage}&search=${searchTerm}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      if (data.success) {
        setKycRequests(data.data.requests);
        setTotalPages(data.data.pagination.pages);
      } else {
        throw new Error(data.error || 'Failed to fetch KYC requests');
      }
    } catch (error) {
      console.error('Error fetching KYC requests:', error);
      alert(error.message || 'Failed to fetch KYC requests');
    } finally {
      setIsLoading(false);
    }
  };

  const handleStatusChange = async (id, newStatus, type = 'information') => {
    try {
      // If approving KYC information, show fee dialog instead of immediate change
      if (type === 'information' && newStatus === 'approved') {
        setPendingStatusChange({ id, newStatus, type });
        setIsFeeDialogOpen(true);
        return;
      }
      
      await processStatusChange(id, newStatus, type);
    } catch (error) {
      setStatusMessage({
        type: 'error',
        message: error.message
      });

      setTimeout(() => {
        setStatusMessage({ type: '', message: '' });
      }, 3000);
    }
  };

  // New function to process the actual status change after fee dialog is completed
  const processStatusChange = async (id, newStatus, type, fees = null) => {
    const token = localStorage.getItem('superadminToken');
    
    let requestBody = { 
      status: newStatus,
      comments: '' 
    };
    
    // Add custom fees if provided
    if (fees) {
      requestBody = {
        ...requestBody,
        platformFee: parseFloat(fees.platformFee),
        monthlyFee: parseFloat(fees.monthlyFee)
      };
    }
    
    const endpoint = type === 'document' 
      ? `${API_URL}/kyc/admin/document/${id}/status`
      : `${API_URL}/kyc/admin/information/${id}/status`;

    const response = await fetch(endpoint, {
      method: 'PATCH',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(requestBody)
    });

    const data = await response.json();
    
    if (!response.ok) {
      throw new Error(data.message || data.error || `Failed to update ${type} status`);
    }

    if (!data.success) {
      throw new Error(data.error || `Status update was not successful`);
    }

    // Update local state directly for document status
    if (type === 'document' && selectedRequest) {
      setSelectedRequest(prev => ({
        ...prev,
        documents: prev.documents.map(doc => 
          doc.id === id ? { ...doc, status: newStatus } : doc
        )
      }));
    }

    // Show success message
    setStatusMessage({
      type: 'success',
      message: `${type.charAt(0).toUpperCase() + type.slice(1)} status updated successfully`
    });

    // Clear message after 3 seconds
    setTimeout(() => {
      setStatusMessage({ type: '', message: '' });
    }, 3000);

    // Only fetch if it's an information status update
    if (type === 'information') {
      await fetchKycRequests();
    }
  };

  // Handle fee dialog submission
  const handleFeeDialogSubmit = async (useCustomFees) => {
    try {
      if (pendingStatusChange) {
        const { id, newStatus, type } = pendingStatusChange;
        
        if (useCustomFees) {
          await processStatusChange(id, newStatus, type, customFees);
        } else {
          await processStatusChange(id, newStatus, type);
        }
      }
    } catch (error) {
      setStatusMessage({
        type: 'error',
        message: error.message
      });
    } finally {
      setIsFeeDialogOpen(false);
      setPendingStatusChange(null);
    }
  };

  // Handle fee input changes
  const handleFeeChange = (e) => {
    const { name, value } = e.target;
    setCustomFees(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleViewDetails = async (userId) => {
    try {
      const token = localStorage.getItem('superadminToken');
      const response = await fetch(
        `${API_URL}/kyc/admin/request/${userId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to fetch KYC details');

      setSelectedRequest(data.data);
      setIsModalOpen(true);
    } catch (err) {
      alert('Failed to fetch KYC details');
    }
  };

  const getImageUrl = (docId) => {
    const token = localStorage.getItem('superadminToken');
    return {
      url: `${API_URL}/kyc/documents/${docId}/view`,
      headers: {
        'Authorization': `Bearer ${token}`
      }
    };
  };

  // Function to handle document viewing
  const handleDocumentView = async (doc, action = 'view') => {
    try {
      const token = localStorage.getItem('superadminToken');
      const response = await fetch(`${API_URL}/kyc/documents/${doc.id}/view`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Failed to fetch document' }));
        throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
      }

      const contentType = response.headers.get('content-type');
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);

      if (action === 'view') {
        const newWindow = window.open('', '_blank');
        newWindow.document.write(`
          <html>
            <head>
              <title>Document Preview - ${doc.document_type}</title>
              <style>
                body { 
                  margin: 0; 
                  padding: 0;
                  display: flex; 
                  flex-direction: column;
                  justify-content: center; 
                  align-items: center; 
                  min-height: 100vh; 
                  background: #f0f0f0; 
                  font-family: Arial, sans-serif;
                  overflow: hidden;
                }
                .container {
                  width: 95vw;
                  height: 95vh;
                  display: flex;
                  flex-direction: column;
                  background: white;
                  padding: 15px;
                  border-radius: 8px;
                  box-shadow: 0 4px 8px rgba(0,0,0,0.1);
                  overflow: hidden;
                }
                .document-header {
                  padding: 10px 0;
                  border-bottom: 1px solid #eee;
                  margin-bottom: 15px;
                  text-align: center;
                }
                .document-view {
                  flex: 1;
                  overflow: hidden;
                  display: flex;
                  justify-content: center;
                  align-items: center;
                }
                img { 
                  max-width: 100%; 
                  max-height: 100%; 
                  object-fit: contain;
                }
                iframe {
                  width: 100%;
                  height: 100%;
                  border: none;
                }
                .title {
                  margin: 0;
                  padding: 10px;
                  color: #333;
                  font-size: 18px;
                  font-weight: bold;
                }
                .close-btn {
                  position: absolute;
                  top: 15px;
                  right: 20px;
                  background: #f44336;
                  color: white;
                  border: none;
                  border-radius: 4px;
                  padding: 8px 12px;
                  cursor: pointer;
                  font-size: 14px;
                }
                .close-btn:hover {
                  background: #d32f2f;
                }
              </style>
            </head>
            <body>
              <div class="container">
                <div class="document-header">
                  <h2 class="title">${doc.document_type}</h2>
                  <button class="close-btn" onclick="window.close()">Close</button>
                </div>
                <div class="document-view">
                  ${contentType.includes('image') 
                    ? `<img src="${url}" alt="Document Preview">`
                    : `<iframe src="${url}" type="${contentType}"></iframe>`
                  }
                </div>
              </div>
            </body>
          </html>
        `);
        newWindow.document.close();
      } else {
        const link = document.createElement('a');
        link.href = url;
        link.download = doc.document_file.split('\\').pop();
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }

      setTimeout(() => window.URL.revokeObjectURL(url), 100);
    } catch (error) {
      alert(error.message || `Failed to ${action} document`);
    }
  };

  // Add loading and error states to UI
  if (isLoading) {
    return <div className="p-6">Loading KYC requests...</div>;
  }

  return (
    <div className="bg-white p-2 rounded-lg shadow-lg">
      <div className="mx-auto max-w-full">
        <div className="bg-white py-10">
          <div className="px-4 sm:px-4 lg:px-4">
            <div className="sm:flex sm:items-center">
              <div className="sm:flex-auto">
                <h1 className="text-xl font-bold bg-gradient-to-r from-blue-400 to-purple-500 bg-clip-text text-transparent">
                  KYC Documents Management
                </h1>
              </div>

              {/* Status Message */}
              {statusMessage.message && (
                <div
                  className={`fixed top-4 right-4 z-50 p-4 rounded-md shadow-lg ${
                    statusMessage.type === "success"
                      ? "bg-green-50 text-green-800 border border-green-200"
                      : "bg-red-50 text-red-800 border border-red-200"
                  }`}
                >
                  <div className="flex items-center">
                    {statusMessage.type === "success" ? (
                      <svg
                        className="w-5 h-5 mr-2"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                          clipRule="evenodd"
                        />
                      </svg>
                    ) : (
                      <svg
                        className="w-5 h-5 mr-2"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                          clipRule="evenodd"
                        />
                      </svg>
                    )}
                    {statusMessage.message}
                  </div>
                </div>
              )}

              <div className="mt-4 sm:mt-0 sm:flex sm:items-center sm:space-x-4">
                <input
                  type="text"
                  placeholder="Search..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="block w-full rounded-md border border-gray-700 placeholder:text-gray-900 text-gray-900 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm px-4 py-2"
                />
                <select
                  value={selectedStatus}
                  onChange={(e) => setSelectedStatus(e.target.value)}
                  className="block w-full rounded-md border border-gray-700 text-gray-900 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm px-4 py-2"
                >
                  <option value="all">All Status</option>
                  <option value="reviewing">Reviewing</option>
                  <option value="approved">Approved</option>
                  <option value="rejected">Rejected</option>
                </select>
              </div>
            </div>

            {/* KYC Requests Table */}
            <div className="mt-8 flow-root">
              <div className="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8 custom-scrollbar">
                <div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
                  <table className="min-w-full divide-y divide-gray-900">
                    <thead>
                      <tr>
                        <th className="px-3 py-3.5 text-left text-xs font-medium text-black sm:pl-0">
                          User
                        </th>
                        <th className="px-3 py-3.5 text-left text-xs font-medium text-black">
                          Documents
                        </th>
                        <th className="px-3 py-3.5 text-left text-xs font-medium text-black">
                          Status
                        </th>
                        <th className="px-3 py-3.5 text-left text-xs font-medium text-black">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-800">
                      {kycRequests.map((request) => (
                        <tr key={request.id}>
                          <td className="whitespace-nowrap py-4 pl-4 pr-3 text-xs text-gray-900 sm:pl-0">
                            <div className="flex flex-col">
                              <span className="font-medium">
                                {request.user_name}
                              </span>
                              <span className="text-gray-500">
                                {request.email}
                              </span>
                            </div>
                          </td>
                          <td className="whitespace-nowrap py-4 pl-4 pr-3 text-xs text-gray-900">
                            <div className="flex flex-col">
                              <span>Total: {request.total_documents || 0}</span>
                              <span className="text-gray-500">
                                Approved: {request.approved_documents || 0} |
                                Pending: {request.pending_documents || 0} |
                                Rejected: {request.rejected_documents || 0}
                              </span>
                            </div>
                          </td>
                          <td className="whitespace-nowrap py-4 px-3 text-xs">
                            <span
                              className={`px-4 py-1.5 text-sm font-semibold rounded-md ${
                                request.status === "approved"
                                  ? "bg-green-100 text-green-800"
                                  : request.status === "rejected"
                                  ? "bg-red-100 text-red-800"
                                  : "bg-blue-100 text-blue-800"
                              }`}
                            >
                              {request.status}
                            </span>
                          </td>
                          <td className="py-4 flex space-x-2 items-center">
                            {/* View Details Button */}
                            <button
                              onClick={() => handleViewDetails(request.user_id)}
                              className="inline-flex items-center px-3 py-1.5 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                              title="View Details"
                            >
                              <svg
                                className="w-4 h-4 mr-2"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth="2"
                                  d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                                />
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth="2"
                                  d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                                />
                              </svg>
                              View Details
                            </button>

                            {/* Status Update Select */}
                            <select
                              onChange={(e) =>
                                handleStatusChange(
                                  request.user_id,
                                  e.target.value
                                )
                              }
                              className="inline-flex items-center px-3 py-1.5 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                              value={request.status}
                            >
                              <option value="reviewing">Reviewing</option>
                              <option value="approved">Approve</option>
                              <option value="rejected">Reject</option>
                            </select>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="mt-4 flex items-center justify-between px-6 py-2">
                <div className="flex items-center text-sm text-gray-900">
                  <span>
                    Page {currentPage} of {totalPages}
                  </span>
                </div>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() =>
                      setCurrentPage((prev) => Math.max(prev - 1, 1))
                    }
                    disabled={currentPage === 1}
                    className={`px-3 py-1 rounded ${
                      currentPage === 1
                        ? "bg-gray-700 text-gray-400 cursor-not-allowed"
                        : "bg-blue-600 text-white hover:bg-blue-700"
                    }`}
                  >
                    Previous
                  </button>
                  <button
                    onClick={() => setCurrentPage((prev) => prev + 1)}
                    disabled={currentPage === totalPages}
                    className={`px-3 py-1 rounded ${
                      currentPage === totalPages
                        ? "bg-gray-700 text-gray-400 cursor-not-allowed"
                        : "bg-blue-600 text-white hover:bg-blue-700"
                    }`}
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Custom Fee Dialog */}
      {isFeeDialogOpen && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 z-50 flex items-center justify-center">
          <div className="bg-white rounded-lg p-6 w-full max-w-md mx-auto shadow-xl">
            <div className="mb-4">
              <h3 className="text-lg font-semibold text-gray-900">
                Set KYC Fees
              </h3>
              <p className="text-sm text-gray-500 mt-1">
                Would you like to set custom KYC fees for this user?
              </p>
            </div>

            <div className="mt-6 space-y-4">
              <div>
                <label
                  htmlFor="platformFee"
                  className="block text-sm font-medium text-gray-700"
                >
                  KYC Approval Fee (One-time)
                </label>
                <div className="mt-1 relative rounded-md shadow-sm">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <span className="text-gray-500 sm:text-sm">$</span>
                  </div>
                  <input
                    type="number"
                    name="platformFee"
                    id="platformFee"
                    className="focus:ring-indigo-500 focus:border-indigo-500 block w-full pl-7 pr-12 sm:text-sm border-gray-300 rounded-md"
                    placeholder="0.00"
                    step="0.01"
                    min="0"
                    value={customFees.platformFee}
                    onChange={handleFeeChange}
                  />
                </div>
              </div>

              <div>
                <label
                  htmlFor="monthlyFee"
                  className="block text-sm font-medium text-gray-700"
                >
                  Monthly Platform Fee
                </label>
                <div className="mt-1 relative rounded-md shadow-sm">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <span className="text-gray-500 sm:text-sm">$</span>
                  </div>
                  <input
                    type="number"
                    name="monthlyFee"
                    id="monthlyFee"
                    className="focus:ring-indigo-500 focus:border-indigo-500 block w-full pl-7 pr-12 sm:text-sm border-gray-300 rounded-md"
                    placeholder="0.00"
                    step="0.01"
                    min="0"
                    value={customFees.monthlyFee}
                    onChange={handleFeeChange}
                  />
                </div>
              </div>
            </div>

            <div className="mt-6 flex justify-end space-x-3">
              <button
                type="button"
                className="inline-flex justify-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                onClick={() => handleFeeDialogSubmit(false)}
              >
                Use Default Fees
              </button>
              <button
                type="button"
                className="inline-flex justify-center px-4 py-2 text-sm font-medium text-white bg-indigo-600 border border-transparent rounded-md shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                onClick={() => handleFeeDialogSubmit(true)}
              >
                Apply Custom Fees
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Details Modal */}
      {isModalOpen && selectedRequest && (
        <div
          className="fixed inset-0 bg-gray-600 bg-opacity-50 z-50 overflow-y-auto h-full w-full flex items-center justify-center"
          onClick={() => {
            setIsModalOpen(false);
            setSelectedRequest(null);
          }}
        >
          <div
            className="relative mx-auto p-5 w-full max-w-4xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="bg-white rounded-lg shadow-lg relative">
              {/* Close button - Moved outside the header for better visibility */}
              <button
                onClick={() => {
                  setIsModalOpen(false);
                  setSelectedRequest(null);
                }}
                className="absolute right-4 top-4 z-50 p-2 rounded-full hover:bg-gray-100 transition-colors duration-200"
                title="Close"
              >
                <svg
                  className="w-6 h-6 text-gray-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>

              {/* Modal Header */}
              <div className="p-4 rounded-t-lg flex items-center border-b border-gray-200">
                <h2 className="text-xl font-bold bg-gradient-to-r from-blue-400 to-purple-500 bg-clip-text text-transparent">
                  KYC Details
                </h2>
              </div>

              {/* Modal Content with custom scrollbar */}
              <div className="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
                <div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
                  <div className="max-h-[calc(100vh-200px)] overflow-y-auto custom-scrollbar">
                    <div className="px-4 py-5 sm:p-6">
                      {/* Fees Section */}
                      {selectedRequest.status === "approved" && (
                        <div className="mb-4">
                          <h3 className="text-lg font-semibold mb-4 text-gray-900 border-b pb-2">
                            Applied Charges
                          </h3>
                          <div className="bg-gray-50 rounded-lg shadow-sm">
                            <table className="min-w-full divide-y divide-gray-200">
                              <thead>
                                <tr>
                                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Fee Type
                                  </th>
                                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Amount
                                  </th>
                                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Frequency
                                  </th>
                                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Status
                                  </th>
                                </tr>
                              </thead>
                              <tbody className="bg-white divide-y divide-gray-200">
                                {/* Platform Fee Row */}
                                <tr>
                                  <td className="px-4 py-3 text-sm text-gray-900">
                                    KYC Platform Fee
                                  </td>
                                  <td className="px-4 py-3 text-sm text-gray-900">
                                    ${selectedRequest.platformFee || "5.00"}
                                  </td>
                                  <td className="px-4 py-3 text-sm text-gray-900">
                                    One-time
                                  </td>
                                  <td className="px-4 py-3 text-sm">
                                    <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                                      Paid
                                    </span>
                                  </td>
                                </tr>
                                {/* Monthly Fee Row */}
                                <tr>
                                  <td className="px-4 py-3 text-sm text-gray-900">
                                    Monthly Platform Fee
                                  </td>
                                  <td className="px-4 py-3 text-sm text-gray-900">
                                    ${selectedRequest.monthlyFee || "5.00"}
                                  </td>
                                  <td className="px-4 py-3 text-sm text-gray-900">
                                    Monthly
                                  </td>
                                  <td className="px-4 py-3 text-sm">
                                    <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800">
                                      Active
                                    </span>
                                  </td>
                                </tr>
                              </tbody>
                            </table>
                          </div>
                        </div>
                      )}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {/* Personal Information Section */}
                        <div className="bg-gray-50 rounded-lg shadow-sm">
                          <h3 className="text-lg font-semibold mb-4 text-gray-900 border-b pb-2">
                            Personal Information
                          </h3>
                          <div className="space-y-3">
                            <div className="flex justify-between items-center">
                              <span className="text-gray-600 text-sm font-medium">
                                Name:
                              </span>
                              <span className="text-sm text-gray-900">
                                {selectedRequest.name}
                              </span>
                            </div>
                            <div className="flex justify-between items-center">
                              <span className="text-gray-600 text-sm font-medium">
                                Email:
                              </span>
                              <span className="text-sm text-gray-900">
                                {selectedRequest.email}
                              </span>
                            </div>
                            <div className="flex justify-between items-center">
                              <span className="text-gray-600 text-sm font-medium">
                                Mobile:
                              </span>
                              <span className="text-sm text-gray-900">
                                {selectedRequest.mobile_number}
                              </span>
                            </div>
                            <div className="flex justify-between items-center">
                              <span className="text-gray-600 text-sm font-medium">
                                Nationality:
                              </span>
                              <span className="text-sm text-gray-900">
                                {selectedRequest.nationality}
                              </span>
                            </div>
                            <div className="flex justify-between items-center">
                              <span className="text-gray-600 text-sm font-medium">
                                Date of Birth:
                              </span>
                              <span className="text-sm text-gray-900">
                                {selectedRequest.date_of_birth}
                              </span>
                            </div>
                            <div className="flex justify-between items-center">
                              <span className="text-gray-600 text-sm font-medium">
                                Gender:
                              </span>
                              <span className="text-sm text-gray-900">
                                {selectedRequest.gender}
                              </span>
                            </div>
                          </div>
                        </div>

                        {/* Address Section */}
                        <div className="bg-gray-50 rounded-lg shadow-sm">
                          <h3 className="text-lg font-semibold mb-4 text-gray-900 border-b pb-2">
                            Address Details
                          </h3>
                          <div className="space-y-3">
                            <div className="flex justify-between items-center">
                              <span className="text-gray-600 text-sm font-medium">
                                Address Line 1:
                              </span>
                              <span className="text-sm text-gray-900">
                                {selectedRequest.address_line1}
                              </span>
                            </div>
                            {selectedRequest.address_line2 && (
                              <div className="flex justify-between items-center">
                                <span className="text-gray-600 text-sm font-medium">
                                  Address Line 2:
                                </span>
                                <span className="text-sm text-gray-900">
                                  {selectedRequest.address_line2}
                                </span>
                              </div>
                            )}
                            <div className="flex justify-between items-center">
                              <span className="text-gray-600 text-sm font-medium">
                                City:
                              </span>
                              <span className="text-sm text-gray-900">
                                {selectedRequest.city}
                              </span>
                            </div>
                            <div className="flex justify-between items-center">
                              <span className="text-gray-600 text-sm font-medium">
                                State:
                              </span>
                              <span className="text-sm text-gray-900">
                                {selectedRequest.state}
                              </span>
                            </div>
                            <div className="flex justify-between items-center">
                              <span className="text-gray-600 text-sm font-medium">
                                Postal Code:
                              </span>
                              <span className="text-sm text-gray-900">
                                {selectedRequest.postal_code}
                              </span>
                            </div>
                            <div className="flex justify-between items-center">
                              <span className="text-gray-600 text-sm font-medium">
                                Country:
                              </span>
                              <span className="text-sm text-gray-900">
                                {selectedRequest.country}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Documents Section */}
                      <div className="mt-6">
                        <h3 className="text-lg font-semibold mb-4 text-gray-900 border-b pb-2">
                          Uploaded Documents
                        </h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          {selectedRequest.documents?.map((doc, index) => (
                            <div
                              key={index}
                              className="bg-gray-50 rounded-lg p-4 shadow-sm border border-gray-200 hover:shadow-md transition-shadow duration-200"
                            >
                              <div className="flex justify-between items-start mb-4">
                                <div>
                                  <h4 className="font-medium text-gray-900 mb-1">
                                    {doc.document_type}
                                  </h4>
                                  {/* <p className="text-sm text-gray-500">
                                    Document ID: {doc.document_number}
                                  </p> */}
                                  <p className="text-sm text-gray-500">
                                    Uploaded:{" "}
                                    {doc.created_at
                                      ? new Date(
                                          doc.created_at
                                        ).toLocaleDateString()
                                      : "Date not available"}
                                  </p>
                                </div>
                                <span
                                  className={`px-3 py-1 text-xs font-semibold rounded-full flex items-center
                                  ${
                                    doc.status === "approved"
                                      ? "bg-green-100 text-green-800 border border-green-200"
                                      : doc.status === "rejected"
                                      ? "bg-red-100 text-red-800 border border-red-200"
                                      : "bg-blue-100 text-blue-800 border border-blue-200"
                                  }`}
                                >
                                  <span
                                    className={`w-2 h-2 rounded-full mr-2
                                    ${
                                      doc.status === "approved"
                                        ? "bg-green-500"
                                        : doc.status === "rejected"
                                        ? "bg-red-500"
                                        : "bg-blue-500"
                                    }`}
                                  />
                                  {doc.status.charAt(0).toUpperCase() +
                                    doc.status.slice(1)}
                                </span>
                              </div>

                              {/* Document Preview */}
                              <div className="mb-4 p-2 bg-gray-100 rounded-lg border border-gray-200">
                                <div className="flex items-center text-sm text-gray-600">
                                  <svg
                                    className="w-5 h-5 mr-2"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth="2"
                                      d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z"
                                    />
                                  </svg>
                                  {doc.document_file.split("\\").pop()}
                                </div>
                              </div>

                              {/* Action Buttons */}
                              <div className="space-y-3">
                                {/* View/Download Buttons */}
                                <div className="flex space-x-2">
                                  <button
                                    onClick={() => handleDocumentView(doc)}
                                    className="flex-1 inline-flex justify-center items-center px-3 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                                  >
                                    <svg
                                      className="w-4 h-4 mr-2"
                                      fill="none"
                                      stroke="currentColor"
                                      viewBox="0 0 24 24"
                                    >
                                      <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth="2"
                                        d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                                      />
                                      <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth="2"
                                        d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                                      />
                                    </svg>
                                    View
                                  </button>
                                  <button
                                    onClick={() =>
                                      handleDocumentView(doc, "download")
                                    }
                                    className="flex-1 inline-flex justify-center items-center px-3 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                                  >
                                    <svg
                                      className="w-4 h-4 mr-2"
                                      fill="none"
                                      stroke="currentColor"
                                      viewBox="0 0 24 24"
                                    >
                                      <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth="2"
                                        d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
                                      />
                                    </svg>
                                    Download
                                  </button>
                                </div>

                                {/* Status Update Buttons */}
                                <div className="flex space-x-2">
                                  <button
                                    onClick={() =>
                                      handleStatusChange(
                                        doc.id,
                                        "approved",
                                        "document"
                                      )
                                    }
                                    disabled={doc.status === "approved"}
                                    className={`flex-1 inline-flex justify-center items-center px-3 py-2 border text-sm font-medium rounded-md
                                      ${
                                        doc.status === "approved"
                                          ? "bg-green-50 text-green-700 border-green-200 cursor-not-allowed"
                                          : "border-green-500 text-green-700 hover:bg-green-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                                      }`}
                                  >
                                    <svg
                                      className="w-4 h-4 mr-2"
                                      fill="none"
                                      stroke="currentColor"
                                      viewBox="0 0 24 24"
                                    >
                                      <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth="2"
                                        d="M5 13l4 4L19 7"
                                      />
                                    </svg>
                                    Approve
                                  </button>
                                  <button
                                    onClick={() =>
                                      handleStatusChange(
                                        doc.id,
                                        "rejected",
                                        "document"
                                      )
                                    }
                                    disabled={doc.status === "rejected"}
                                    className={`flex-1 inline-flex justify-center items-center px-3 py-2 border text-sm font-medium rounded-md
                                      ${
                                        doc.status === "rejected"
                                          ? "bg-red-50 text-red-700 border-red-200 cursor-not-allowed"
                                          : "border-red-500 text-red-700 hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                                      }`}
                                  >
                                    <svg
                                      className="w-4 h-4 mr-2"
                                      fill="none"
                                      stroke="currentColor"
                                      viewBox="0 0 24 24"
                                    >
                                      <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth="2"
                                        d="M6 18L18 6M6 6l12 12"
                                      />
                                    </svg>
                                    Reject
                                  </button>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}